import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Spin,
  message,
  Menu,
  Typography,
  Space,
} from 'antd';
import {
  LineChartOutlined,
  CarOutlined,
  ClusterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import DateRangeFilter from '../../components/DateRangeFilter';

const { Title, Text } = Typography;

const initialDateFilter = {
  year: dayjs().year(),
  month: undefined, // Default to all months
  dateRange: undefined
};

const ResourceUtilization = () => {
  const [resourceData, setResourceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(initialDateFilter);

  const fetchResourceData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters based on date filter
      const params = {};
      if (dateFilter.dateRange && dateFilter.dateRange[0] && dateFilter.dateRange[1]) {
        params.startDate = dateFilter.dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateFilter.dateRange[1].format('YYYY-MM-DD');
      } else if (
        typeof dateFilter.year !== 'undefined' &&
        typeof dateFilter.month !== 'undefined'
      ) {
        params.year = dateFilter.year;
        params.month = dateFilter.month;
      } else if (typeof dateFilter.year !== 'undefined') {
        params.year = dateFilter.year;
      }
      
      console.log('Resource Utilization API params:', params);
      const response = await apiService.insights.getResources(params);
      setResourceData(response.data);
    } catch (error) {
      console.error('Error fetching resource data:', error);
      message.error('Failed to fetch resource utilization data');
    } finally {
      setLoading(false);
    }
  }, [dateFilter.year, dateFilter.month, dateFilter.dateRange]);

  useEffect(() => {
    fetchResourceData();
  }, [fetchResourceData]);

  const handleDateFilterChange = (newValue) => {
    setDateFilter(newValue);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Resource Utilization</Title>
        <Text type="secondary">Monitor driver, van, and channel performance metrics</Text>
      </div>

      <Menu
        mode="horizontal"
        selectedKeys={['resources']}
        style={{ marginBottom: '24px' }}
      >
        <Menu.Item key="dashboard" icon={<LineChartOutlined />}>
          <Link to="/reports">Dashboard</Link>
        </Menu.Item>
        <Menu.Item key="financial" icon={<span style={{ fontSize: '16px' }}>£</span>}>
          <Link to="/reports/financial">Financial</Link>
        </Menu.Item>
        <Menu.Item key="van" icon={<CarOutlined />}>
          <Link to="/reports/van">Vans</Link>
        </Menu.Item>
        <Menu.Item key="resources" icon={<ClusterOutlined />}>
          <Link to="/reports/resources">Resource Utilization</Link>
        </Menu.Item>
      </Menu>

      {/* Date Filter */}
      <Card title="Filters" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle" style={{ flexWrap: 'wrap' }}>
          <Col xs={24} sm={12} md={10} lg={8} xl={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>Date Range:</Text>
              <DateRangeFilter 
                value={dateFilter} 
                onChange={handleDateFilterChange} 
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Driver Utilization">
            <Table
              dataSource={resourceData?.driverUtilization || []}
              columns={[
                { title: 'Driver', dataIndex: 'driverName', key: 'driverName' },
                { title: 'Total Jobs', dataIndex: 'totalJobs', key: 'totalJobs' },
                { 
                  title: 'Salary Earned', 
                  dataIndex: 'salaryEarned', 
                  key: 'salaryEarned',
                  render: (value) => formatCurrency(value || 0)
                },
              ]}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Van Utilization">
            <Table
              dataSource={resourceData?.vanUtilization || []}
              columns={[
                { title: 'Registration', dataIndex: 'registration', key: 'registration' },
                { 
                  title: 'Status', 
                  dataIndex: 'status', 
                  key: 'status',
                  render: (status) => (
                    <Tag color={status === 'available' ? 'green' : status === 'in-use' ? 'blue' : 'red'}>
                      {status}
                    </Tag>
                  )
                },
                { title: 'Total Jobs', dataIndex: 'totalJobs', key: 'totalJobs' },
              ]}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24}>
          <Card title="Channel Performance">
            <Table
              dataSource={resourceData?.channelPerformance || []}
              columns={[
                { title: 'Channel', dataIndex: 'channelName', key: 'channelName' },
                { title: 'Total Jobs', dataIndex: 'totalJobs', key: 'totalJobs' },
                { 
                  title: 'Total Revenue', 
                  dataIndex: 'totalRevenue', 
                  key: 'totalRevenue',
                  render: (value) => formatCurrency(value)
                },
                { 
                  title: 'Avg Job Value', 
                  dataIndex: 'averageJobValue', 
                  key: 'averageJobValue',
                  render: (value) => formatCurrency(value)
                },
              ]}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ResourceUtilization;
